import { ContentState, convertToRaw, RichUtils, EditorState } from "draft-js";
import { normaliseTableData } from "./tableUtils";
import { setBlockType } from "./draftUtils";

import { DOCUMENTS } from "../constants";
import { findClosestSize } from "../constants/fontSizes";
import { HEADER_FONT_SIZES } from "../constants/document";

import applyFontStyle from "../components/CustomRevvToolbar/applyFontStyle";

const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;

export const getDraftFormFields = sectionData => {
  if (!sectionData || typeof sectionData === "string") return [];
  const data =
    sectionData instanceof ContentState
      ? convertToRaw(sectionData)
      : sectionData;

  const { entityMap = {} } = data;
  const fieldList = [];
  Object.values(entityMap).forEach(entity => {
    if (entity.type === "DYNAMICFIELD") {
      const { id, dynamicFieldId, selectedText } = entity.data;
      const field = {
        id,
        dynamicFieldId,
        selectedText,
        type: "DYNAMICFIELD",
      };
      fieldList.push(field);
    }
  });
  return fieldList;
};

export const getTableFormFields = sectionData => {
  const tableData = normaliseTableData(
    sectionData,
    null,
    sectionData.attributes.hiddenCols,
  );
  const { data } = tableData;
  const fieldList = [];
  if (Array.isArray(data) && data.length > 0) {
    data.forEach(row => {
      const { data: rowData } = row;
      if (Array.isArray(rowData) && rowData.length > 0) {
        rowData.forEach(cell => {
          const { data: cellData } = cell;
          if (cellData instanceof Object) {
            fieldList.push(...getDraftFormFields(cellData.data));
          }
        });
      }
    });
  }
  return fieldList;
};

export const getSectionFormFields = section => {
  if (section) {
    const { id, type, data, clauseOptions } = section;
    if (type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
      const coverpageFieldLists = [];
      Object.keys(section.data.components).forEach(componentid => {
        const sec = section.data.components[componentid].content;
        coverpageFieldLists.push(...getSectionFormFields(sec));
      });
      return coverpageFieldLists;
    }
    if (type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
      const headerFooterFieldList = [];
      Object.keys(section.data.header.components).forEach(componentid => {
        const sec = section.data.header.components[componentid].content;
        headerFooterFieldList.push(...getSectionFormFields(sec));
      });
      Object.keys(section.data.footer.components).forEach(componentid => {
        const sec = section.data.footer.components[componentid].content;
        headerFooterFieldList.push(...getSectionFormFields(sec));
      });
      return headerFooterFieldList;
    }
    const formFields = [];
    let fields = [];
    // get form fields from dynamic field
    if (type === DOCUMENT_COMPONENT_TYPES.HTML) {
      fields = getDraftFormFields(data);
    } else if (type === DOCUMENT_COMPONENT_TYPES.TABLE) {
      fields = getTableFormFields(data);
    }

    // get form fields from clauseoptions

    if (clauseOptions) {
      const { id: optionID, clauseValues } = clauseOptions;
      formFields.push({
        id: optionID,
        dynamicFieldId: optionID,
        type: "CLAUSE",
      });

      if (Array.isArray(clauseValues) && clauseValues.length > 0) {
        // const filterClauseValues = clauseValues.filter(cl => {
        //   if (Array.isArray(value) && value.includes(cl.id)) {
        //     return cl;
        //   }
        //   if (cl.id === value) {
        //     return cl;
        //   }
        //   return false;
        // });

        const tempFeilds = [];
        clauseValues.forEach(clauseValue => {
          getDraftFormFields(JSON.parse(clauseValue.value)).forEach(field => {
            tempFeilds.push({
              ...field,
              clauseFieldMeta: {
                clauseId: optionID,
                sub_type: "CLAUSE_FIELD",
              },
            });
          });
        });
        fields = [...tempFeilds];
      } else if (
        clauseOptions.value &&
        typeof clauseOptions.value === "string"
      ) {
        const tempFeilds = [];
        getDraftFormFields(JSON.parse(clauseOptions.value)).forEach(field => {
          tempFeilds.push({
            ...field,
            clauseFieldMeta: {
              clauseId: optionID,
              sub_type: "CLAUSE_FIELD",
            },
          });
          fields = [...tempFeilds];
        });
      }
    }
    formFields.push(...fields);

    const fieldList = formFields.map(field => ({ ...field, sectionID: id }));
    return fieldList;
  }
  return [];
};

export const getFormFields = components => {
  const fieldList = [];
  Object.keys(components).forEach(componentId => {
    if (componentId) {
      const section = components[componentId].content;
      fieldList.push(...getSectionFormFields(section));
    }
  });
  return fieldList;
};

export const shouldSaveStateImmediately = editorState => {
  const changeTypes = {
    "change-inline-style": true, // when inline style is changed
    "remove-range": true, // when a chunk of text is removed
    "split-block": true, // when enter character
    "apply-entity": true, // when link, DF, etc is added
    "change-block-type": true, // H1, H2, etc.
    "spellcheck-change": true, // grammarly, etc.
    "insert-fragment": true, // paste content
    "backspace-character": false,
    "delete-character": false,
    "insert-characters": false,
  };
  return changeTypes[editorState.getLastChangeType()]
    ? changeTypes[editorState.getLastChangeType()]
    : false;
};

export const checkStylesInSelection = (editorState, type) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const styles = [];

  if (selection.isCollapsed()) {
    editorState
      .getCurrentInlineStyle()
      .forEach(style => style && styles.push(style));
  } else {
    let key = selection.getStartKey();
    let startOffset = selection.getStartOffset();
    const endKey = selection.getEndKey();
    const endOffset = selection.getEndOffset();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const lastRound = key === endKey;
      const block = contentState.getBlockForKey(key);
      const offsetEnd = lastRound ? endOffset : block.getLength();
      const characterList = block.getCharacterList();
      for (
        let offsetIndex = startOffset;
        offsetIndex < offsetEnd;
        offsetIndex += 1
      ) {
        if (characterList.get(offsetIndex)) {
          characterList
            .get(offsetIndex)
            .getStyle()
            .forEach(style => style && styles.push(style));
        }
      }
      if (lastRound) break;
      key = contentState.getKeyAfter(key);
      startOffset = 0;
    }
  }
  const inlineStyles = Array.from(new Set(styles));
  let count = 0;
  if (Array.isArray(inlineStyles) && inlineStyles.length > 0) {
    inlineStyles.forEach(style => {
      if (typeof style === "string" && style.startsWith(type)) {
        count += 1;
      }
    });
  }

  return count;
};

export const applyStyleInEditor = (editorState, metaData, styleType) => {
  if (editorState.getSelection().isCollapsed()) {
    const count = checkStylesInSelection(editorState, styleType);
    if (count > 0) {
      return editorState;
    }

    const {
      FONTSIZE,
      FONTCOLOR,
      FONTFAMILY,
      FONTWEIGHT,
      BOLD,
      ITALIC,
      UNDERLINE,
      BLOCKTYPE,
    } = metaData;

    if (FONTFAMILY === undefined) {
      editorState = applyFontStyle(
        editorState,
        "FONTFAMILY_OPENSANS",
        "FONTFAMILY",
      );
      // Was creating some issues in DF stylings
      // if (BOLD === undefined && FONTWEIGHT === undefined)
      //   editorState = applyFontStyle(
      //     editorState,
      //     "FONTWEIGHT_REGULAR",
      //     "FONTWEIGHT",
      //   );
    }
    if (count === 0 && FONTSIZE && styleType === "FONTSIZE") {
      editorState = applyFontStyle(editorState, FONTSIZE, "FONTSIZE");
    }
    if (count === 0 && FONTCOLOR && styleType === "FONTCOLOR") {
      editorState = applyFontStyle(editorState, FONTCOLOR, "FONTCOLOR");
    }
    if (count === 0 && FONTFAMILY && styleType === "FONTFAMILY") {
      editorState = applyFontStyle(editorState, FONTFAMILY, "FONTFAMILY");
      if (FONTWEIGHT)
        editorState = applyFontStyle(editorState, FONTWEIGHT, "FONTWEIGHT");
    }
    if (count === 0 && BOLD && styleType === "BOLD") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && ITALIC && styleType === "ITALIC") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && UNDERLINE && styleType === "UNDERLINE") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && BLOCKTYPE && styleType === "BLOCKTYPE") {
      editorState = setBlockType(BLOCKTYPE, editorState);

      if (
        BLOCKTYPE === "header-one" ||
        BLOCKTYPE === "header-two" ||
        BLOCKTYPE === "header-three" ||
        BLOCKTYPE === "header-four"
      ) {
        let currentInlineStyle = editorState.getCurrentInlineStyle();
        const inlineArray = [...currentInlineStyle];
        const weightStyle = inlineArray.filter(style =>
          style.includes("FONTWEIGHT"),
        );
        if (weightStyle.length < 1) {
          currentInlineStyle = currentInlineStyle.add("FONTWEIGHT_BOLD");
        }
        const fontSizeArr = inlineArray.filter(style =>
          style.includes("FONTSIZE"),
        );
        if (fontSizeArr.length < 1) {
          const defaultHeaderFontSize = HEADER_FONT_SIZES[BLOCKTYPE];
          const size = findClosestSize(defaultHeaderFontSize);
          if (size && size.name) {
            currentInlineStyle = currentInlineStyle.add(size.name);
          }
        }
        editorState = EditorState.setInlineStyleOverride(
          editorState,
          currentInlineStyle,
        );
      }
    }

    return editorState;
  }
  return editorState;
};

export const sortContentState = rawContentState => {
  const { blocks, entityMap } = rawContentState;
  blocks.forEach(block => {
    // sort inline styles based on offset & style
    if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
      block.inlineStyleRanges.sort((a, b) => {
        if (a.offset === b.offset) {
          return a.style > b.style ? 1 : -1;
        }
        return a.offset > b.offset ? 1 : -1;
      });
    }

    // sort entity ranges by key
    if (block.entityRanges && block.entityRanges.length > 0) {
      block.entityRanges.sort((a, b) => (a.key > b.key ? 1 : -1));
    }
  });

  // sort entityMap by id
  if (entityMap && entityMap.length > 0) {
    entityMap.sort((a, b) => {
      if (!a.data || !a.data.id || !b.data || !b.data.id) {
        return 1; // move to end of array
      }
      return a.data.id > b.data.id ? 1 : -1;
    });
  }
  return rawContentState;
};

export const getDiffList = (oldFields, newFields) => {
  const oldDFIDsList = new Set(oldFields.map(df => df.dynamicFieldId));

  const newDFIDsList = new Set(newFields.map(df => df.dynamicFieldId));

  // get newly added fields in component
  const addedDFsinComponent = [...newDFIDsList].filter(
    df => !oldDFIDsList.has(df),
  );

  // get deleted fields in component
  const deletedDFsinComponent = [...oldDFIDsList].filter(
    df => !newDFIDsList.has(df),
  );

  return { added: addedDFsinComponent, deleted: deletedDFsinComponent };
};

export const getFormFieldsDiffList = (oldSection, newSection) => {
  // get old fields and field id list
  const oldFields = oldSection ? getSectionFormFields(oldSection) : [];
  // get new fields and field id list
  const newFields = newSection ? getSectionFormFields(newSection) : [];
  return getDiffList(oldFields, newFields);
};
